<script setup>
import { h, ref, computed, watch } from 'vue';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { hasConversationPermission } from 'dashboard/helper/conversationPermissions';
import {
  preloadAceleresFunilFrame,
  preloadSidebarPages,
  preloadSidebarDependencies,
} from 'dashboard/helper/sidebarPagePreload';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const { uiSettings } = useUISettings();
const store = useStore();
const companiesStore = useCompaniesStore();
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const router = useRouter();
// Aceleres: papel atual (gestor = administrator) para gatear itens admin-only
const aceleresCurrentRole = useMapGetter('getCurrentRole');
const currentAccount = useMapGetter('getCurrentAccount');
const currentCustomRoleId = useMapGetter('getCurrentCustomRoleId');
const isAceleresAdministrator = computed(
  () => aceleresCurrentRole.value === 'administrator'
);
const currentPermissions = computed(
  () => currentAccount.value?.permissions || []
);
const hasCustomModuleAccess = permission =>
  currentPermissions.value.includes(permission);
const canAccessStandardModule = permission =>
  currentPermissions.value.some(item =>
    ['agent', 'administrator'].includes(item)
  ) ||
  !currentCustomRoleId.value ||
  hasCustomModuleAccess(permission);
const hasPageAccess = permission =>
  !currentPermissions.value.includes('granular_page_access') ||
  !permission ||
  currentPermissions.value.includes(permission);
const canAccessAdminModule = permission =>
  currentPermissions.value.includes('administrator') ||
  (!currentCustomRoleId.value && isAceleresAdministrator.value) ||
  (Boolean(currentCustomRoleId.value) && hasCustomModuleAccess(permission));
// Integracoes NAO e um modulo admin-only: o vendedor entra e cria/administra
// as proprias conexoes. Quem recorta o que ele pode fazer la dentro e o
// backend, por `viewer_is_manager` / `viewer_can_create` / `can_manage`
// (IntegracoesBridgeController + WhatsappInstance), nao este gate. Em
// 29/08/2026 a refatoracao de perfis de acesso normalizou este item para
// `canAccessAdminModule` e derrubou o acesso do vendedor, que estava em
// producao desde 22/08. Se alguem trocar isto por `canAccessAdminModule`, e
// aquela regressao voltando.
// O braco do vendedor exige AUSENCIA de perfil customizado de proposito: quem
// tem `custom_role` nao recebe 'agent' em `permissions` (ver
// enterprise/app/models/enterprise/account_user.rb), entao a rota o julga so
// por `integration_manage`. Sem esta condicao o item apareceria no menu para
// um perfil customizado que a rota devolve ao dashboard.
const canAccessAceleresIntegrations = computed(
  () =>
    currentPermissions.value.includes('agent') ||
    (aceleresCurrentRole.value === 'agent' && !currentCustomRoleId.value) ||
    canAccessAdminModule('integration_manage')
);
const toggleShortcutModalFn = show => {
  if (show && !isAceleresAdministrator.value) {
    return;
  }

  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const expandItem = name => {
  expandedItem.value = name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  expandItem,
  isMobileSidebarOpen: computed(() => props.isMobileSidebarOpen),
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  // Aceleres 2026-08-25: o rato fala em espaco visual, `sidebarWidth` vira um
  // comprimento CSS (layout). Ver helper/dashboardZoom.js.
  const delta = visualToLayout(
    isRTL.value
      ? startX.value - getClientX(event)
      : getClientX(event) - startX.value
  );
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

watch(
  [accountId, currentPermissions],
  () => {
    preloadSidebarDependencies(store);
    preloadSidebarPages({
      store,
      companiesStore,
      uiSettings: uiSettings.value,
      scopeKey: String(accountId.value || 'default'),
    });
    if (
      canAccessAdminModule('portfolio_manage') &&
      hasPageAccess('page_portfolio')
    ) {
      preloadAceleresFunilFrame(
        () =>
          canAccessAdminModule('portfolio_manage') &&
          hasPageAccess('page_portfolio')
      );
    }
  },
  { immediate: true }
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

// Aceleres: o compositor nativo do Chatwoot criava a conversa direto, sem
// selecao de instancia, sem a checagem de lead ja caracterizado e sem marcar o
// atendimento como iniciado -- ou seja, a conversa nascia em "Novos" e podia
// atropelar o dono do lead. Este botao agora leva ao MESMO fluxo do Chat de
// Atendimento (AceleresNewConversationPanel + AceleresStartConversationFlow).
//
// O pedido viaja como query da rota, e nao por evento de barramento: quando o
// usuario esta noutra pagina a ChatList ainda nao existe para escutar, e a
// query sobrevive a navegacao.
const openAceleresNewConversation = () => {
  router.push({
    name: 'home',
    params: { accountId: accountId.value },
    query: { new_conversation: '1' },
  });
};
const newConversationLabel = 'Nova conversa';

const menuItems = computed(() => {
  const items = [
    ...(canAccessAdminModule('team_presence_manage')
      ? [
          {
            name: 'AceleresTeamPresence',
            label: t('SIDEBAR.ACELERES_TEAM_PRESENCE'),
            description: t('SIDEBAR.ACELERES_TEAM_PRESENCE_DESCRIPTION'),
            icon: 'i-lucide-users',
            to: accountScopedRoute('aceleres_team_presence'),
            activeOn: ['aceleres_team_presence'],
            pagePermission: 'page_team_presence',
          },
        ]
      : []),
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      description: t('SIDEBAR.INBOX_DESCRIPTION'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
      pagePermission: 'page_inbox',
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      description: t('SIDEBAR.CONVERSATIONS_DESCRIPTION'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
          pagePermission: 'page_conversations_all',
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
          pagePermission: 'page_conversations_mentions',
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
          pagePermission: 'page_conversations_participating',
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
          pagePermission: 'page_conversations_unattended',
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          pagePermission: 'page_conversations_folders',
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          pagePermission: 'page_conversations_teams',
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          pagePermission: 'page_conversations_channels',
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[20px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: h(LabelTagIcon, { size: 16, class: 'text-n-slate-11' }),
          activeOn: ['conversations_through_label'],
          pagePermission: 'page_conversations_labels',
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h(LabelTagIcon, { color: label.color, size: 12 }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      description: t('SIDEBAR.CAPTAIN_DESCRIPTION'),
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_responses_pending',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
          pagePermission: 'page_captain_faqs',
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
          pagePermission: 'page_captain_documents',
        },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
          pagePermission: 'page_captain_scenarios',
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
          pagePermission: 'page_captain_playground',
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
          pagePermission: 'page_captain_inboxes',
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
          pagePermission: 'page_captain_tools',
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
          pagePermission: 'page_captain_settings',
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      description: t('SIDEBAR.CONTACTS_DESCRIPTION'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
          pagePermission: 'page_contacts_all',
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
          pagePermission: 'page_contacts_active',
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          pagePermission: 'page_contacts_segments',
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: h(LabelTagIcon, { size: 16, class: 'text-n-slate-11' }),
          label: t('SIDEBAR.TAGGED_WITH'),
          pagePermission: 'page_contacts_labels',
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h(LabelTagIcon, { color: label.color, size: 12 }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      description: t('SIDEBAR.COMPANIES_DESCRIPTION'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
          pagePermission: 'page_companies_all',
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      description: t('SIDEBAR.CAMPAIGNS_DESCRIPTION'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
          pagePermission: 'page_campaigns_live_chat',
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
          pagePermission: 'page_campaigns_sms',
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
          pagePermission: 'page_campaigns_whatsapp',
        },
      ],
    },
    {
      name: 'Canned Responses',
      label: t('SIDEBAR.CANNED_RESPONSES'),
      description: t('SIDEBAR.CANNED_RESPONSES_DESCRIPTION'),
      icon: 'i-lucide-message-square-quote',
      to: accountScopedRoute('canned_list'),
      activeOn: ['canned_list'],
      pagePermission: 'page_canned_responses',
    },
    // Aceleres: carteirizacao/funil dentro do proprio painel do dashboard.
    ...(canAccessAdminModule('portfolio_manage')
      ? [
          {
            name: 'Carteirizacao',
            label: 'Carteirização',
            icon: 'i-lucide-folder-kanban',
            to: accountScopedRoute('aceleres_carteirizacao'),
            activeOn: ['aceleres_carteirizacao'],
            pagePermission: 'page_portfolio',
          },
        ]
      : []),
    ...(isAceleresAdministrator.value
      ? [
          {
            name: 'Atendimentos',
            label: 'Atendimentos',
            icon: 'i-lucide-chart-no-axes-combined',
            to: accountScopedRoute('aceleres_attendances'),
            activeOn: ['aceleres_attendances'],
          },
        ]
      : []),
    // Mantido desativado para preservar o menu antigo sem exibi-lo.
    ...(false
      ? [
          {
            name: 'Settings',
            label: t('SIDEBAR.SETTINGS'),
            description: t('SIDEBAR.SETTINGS_DESCRIPTION'),
            icon: 'i-lucide-bolt',
            // Mantém as opções ocultas comentadas para possível reutilização futura.
            // Apenas as três opções abaixo devem chegar ao menu do ícone de engrenagem.
            children: [
              /*
        {
          name: 'Settings Captain',
          label: t('SIDEBAR.CAPTAIN_AI'),
          icon: 'i-woot-captain',
          to: accountScopedRoute('captain_settings_index'),
        },
        {
          name: 'Settings New User',
          label: t('SIDEBAR.NEW_USER'),
          icon: 'i-lucide-user-round-plus',
          activeOn: ['configuracoes_novo_usuario'],
          to: accountScopedRoute('configuracoes_novo_usuario'),
        },
        */
              {
                name: 'Settings Agents',
                label: t('SIDEBAR.AGENTS'),
                icon: 'i-lucide-square-user',
                to: accountScopedRoute('agent_list'),
              },
              {
                name: 'Settings Teams',
                label: t('SIDEBAR.TEAMS'),
                icon: 'i-lucide-users',
                activeOn: [
                  'settings_teams_list',
                  'settings_teams_new',
                  'settings_teams_finish',
                  'settings_teams_add_agents',
                  'settings_teams_show',
                  'settings_teams_edit',
                  'settings_teams_edit_members',
                  'settings_teams_edit_finish',
                ],
                to: accountScopedRoute('settings_teams_list'),
              },
              /*
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          // Aponta para a aba de canais sociais da central de integracoes, que
          // e a superficie mantida. A tela antiga (`social_channels_settings`)
          // ainda descreve o OAuth direto com a Meta, que deixou de existir em
          // 13/08/2026 quando Instagram e Messenger passaram para a WAME --
          // mandar o usuario para la era manda-lo para o fluxo errado.
          name: 'Settings Social Channels',
          label: 'Canais Sociais',
          icon: 'i-lucide-share-2',
          activeOn: ['configuracoes_integracoes', 'social_channels_settings'],
          to: accountScopedRoute(
            'configuracoes_integracoes',
            {},
            { aba: 'social' }
          ),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: h(LabelTagIcon, { size: 16, class: 'text-n-slate-11' }),
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        */
              {
                name: 'Settings Canned Responses',
                label: t('SIDEBAR.CANNED_RESPONSES'),
                icon: 'i-lucide-message-square-quote',
                to: accountScopedRoute('canned_list'),
              },
              {
                name: 'Settings Signature',
                label: t('SIGNATURE_SETTINGS.TITLE'),
                icon: 'i-lucide-signature',
                to: accountScopedRoute('signature_settings'),
              },
              {
                name: 'Settings Team Numbers',
                label: 'Números da Equipe',
                icon: 'i-lucide-users-round',
                to: accountScopedRoute('team_numbers_list'),
              },
              /*
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Aurora IA',
          label: 'Aurora IA',
          icon: 'i-lucide-sparkles',
          to: accountScopedRoute('aurora_ia_settings'),
        },
        {
          name: 'Settings Aurora Handoff',
          label: 'Transição API Oficial',
          icon: 'i-lucide-arrow-left-right',
          to: accountScopedRoute('aurora_handoff_settings'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
        */
            ],
          },
        ]
      : []),
    ...(canAccessAceleresIntegrations.value
      ? [
          {
            name: 'Settings Integrations Hub',
            label: 'Integrações',
            description: 'Conecte WhatsApp, e-mail e canais sociais',
            icon: 'i-lucide-plug-zap',
            activeOn: ['configuracoes_integracoes'],
            to: accountScopedRoute('configuracoes_integracoes'),
            pagePermission: 'page_integrations',
          },
        ]
      : []),
    ...(!canAccessAdminModule('settings_manage')
      ? [
          {
            name: 'SettingsQuickMenu',
            label: t('SIDEBAR.SETTINGS'),
            icon: 'i-lucide-bolt',
            openPopoverOnly: true,
            mobileActiveAccent: true,
            children: [
              {
                name: 'Settings Signature',
                label: t('SIGNATURE_SETTINGS.TITLE'),
                icon: 'i-lucide-signature',
                to: accountScopedRoute('signature_settings'),
              },
            ],
          },
        ]
      : []),
    ...(canAccessAdminModule('settings_manage')
      ? [
          {
            name: 'SettingsQuickMenu',
            label: t('SIDEBAR.SETTINGS'),
            description: t('SIDEBAR.SETTINGS_DESCRIPTION'),
            icon: 'i-lucide-bolt',
            openPopoverOnly: true,
            mobileActiveAccent: true,
            children: [
              {
                name: 'Settings Agents',
                label: t('SIDEBAR.AGENTS'),
                icon: 'i-lucide-square-user',
                to: accountScopedRoute('agent_list'),
                pagePermission: 'page_settings_agents',
              },
              {
                name: 'Settings Teams',
                label: t('SIDEBAR.TEAMS'),
                icon: 'i-lucide-users',
                activeOn: [
                  'settings_teams_list',
                  'settings_teams_new',
                  'settings_teams_finish',
                  'settings_teams_add_agents',
                  'settings_teams_show',
                  'settings_teams_edit',
                  'settings_teams_edit_members',
                  'settings_teams_edit_finish',
                ],
                to: accountScopedRoute('settings_teams_list'),
                pagePermission: 'page_settings_teams',
              },
              {
                name: 'Settings Canned Responses',
                label: t('SIDEBAR.CANNED_RESPONSES'),
                icon: 'i-lucide-message-square-quote',
                to: accountScopedRoute('canned_list'),
                pagePermission: 'page_settings_canned_responses',
              },
              {
                name: 'Settings Signature',
                label: t('SIGNATURE_SETTINGS.TITLE'),
                icon: 'i-lucide-signature',
                to: accountScopedRoute('signature_settings'),
              },
              {
                name: 'Settings Team Numbers',
                label: 'Números da Equipe',
                icon: 'i-lucide-users-round',
                to: accountScopedRoute('team_numbers_list'),
                pagePermission: 'page_settings_team_numbers',
              },
            ],
          },
        ]
      : []),
  ];

  const modulePermissionByName = {
    Inbox: 'inbox_manage',
    Conversation: 'conversation',
    Captain: 'captain_manage',
    Contacts: 'contact_manage',
    Companies: 'company_manage',
    Campaigns: 'campaign_manage',
    'Canned Responses': 'canned_response_manage',
  };

  const filterPageItems = item => {
    if (!hasPageAccess(item.pagePermission)) return null;
    const filteredChildren = item.children
      ?.map(filterPageItems)
      .filter(Boolean);
    return filteredChildren ? { ...item, children: filteredChildren } : item;
  };

  return items
    .map(filterPageItems)
    .filter(Boolean)
    .filter(item => {
      const permission = modulePermissionByName[item.name];
      if (!permission) return true;
      if (permission === 'conversation') {
        if (
          !hasConversationPermission(
            store.getters.getCurrentUser,
            accountId.value,
            'conversation_view'
          )
        )
          return false;
        return (
          !currentCustomRoleId.value ||
          currentPermissions.value.some(value =>
            [
              'agent',
              'administrator',
              'conversation_manage',
              'conversation_unassigned_manage',
              'conversation_participating_manage',
              'conversation_page_access',
            ].includes(value)
          )
        );
      }
      return canAccessStandardModule(permission);
    });
});
</script>

<template>
  <aside
    id="acl-mobile-navigation-panel"
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['[data-mobile-sidebar-launcher]'] },
    ]"
    :aria-hidden="isMobile && !isMobileSidebarOpen"
    class="acl-mobile-navigation-panel bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[250px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'acl-mobile-navigation-panel--open': isMobile && isMobileSidebarOpen,
        'acl-mobile-navigation-panel--closed': isMobile && !isMobileSidebarOpen,
        '!z-[1002]': isMobile && isMobileSidebarOpen,
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full':
          isMobile && !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <Button
          icon="i-lucide-pen-line"
          color="slate"
          size="sm"
          :title="newConversationLabel"
          class="dark:hover:!bg-n-slate-9/30"
          :class="
            isEffectivelyCollapsed
              ? '!size-8 !outline-n-weak !text-n-slate-11'
              : '!h-7 !outline-n-weak !text-n-slate-11'
          "
          @click="openAceleresNewConversation"
        />
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <template v-for="item in menuItems" :key="item.name">
          <!-- Aceleres: item de link externo (target=_blank), bypassa SidebarGroup que usa router-link -->
          <li
            v-if="item.externalLink"
            class="grid gap-1 text-sm cursor-pointer select-none min-w-0"
            :class="{ 'items-center': isEffectivelyCollapsed }"
          >
            <a
              :href="item.externalLink"
              :target="item.target || '_blank'"
              rel="noopener noreferrer"
              :title="item.label"
              class="flex items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
              :class="
                isEffectivelyCollapsed
                  ? 'justify-center size-10'
                  : 'gap-2 px-1.5 py-1 h-8 min-w-0'
              "
            >
              <span
                v-if="item.icon"
                class="size-4 flex-shrink-0"
                :class="item.icon"
              />
              <span
                v-if="!isEffectivelyCollapsed"
                class="truncate text-body-main"
              >
                {{ item.label }}
              </span>
            </a>
          </li>
          <SidebarGroup v-else v-bind="item" />
        </template>
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="toggleShortcutModalFn(true)"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>

<style scoped>
@media (max-width: 767px) {
  #acl-mobile-navigation-panel.acl-mobile-navigation-panel {
    position: fixed !important;
    top: auto !important;
    right: auto !important;
    bottom: calc(166px + env(safe-area-inset-bottom, 0px)) !important;
    left: max(12px, env(safe-area-inset-left, 0px)) !important;
    width: min(19rem, calc(100vw - 24px)) !important;
    height: min(68dvh, 34rem) !important;
    max-height: calc(
      100dvh - 188px - env(safe-area-inset-top, 0px) -
        env(safe-area-inset-bottom, 0px)
    ) !important;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.14) !important;
    border-radius: 20px;
    box-shadow: 0 22px 54px rgba(15, 23, 42, 0.24) !important;
    transform-origin: left bottom;
    transition:
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 140ms ease,
      visibility 140ms ease !important;
  }

  #acl-mobile-navigation-panel.acl-mobile-navigation-panel--open {
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    transform: translateY(0) scale(1) !important;
  }

  #acl-mobile-navigation-panel.acl-mobile-navigation-panel--closed {
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    transform: translateY(18px) scale(0.96) !important;
  }

  :global([dir='rtl'])
    #acl-mobile-navigation-panel.acl-mobile-navigation-panel {
    right: max(12px, env(safe-area-inset-right, 0px)) !important;
    left: auto !important;
    transform-origin: right bottom;
  }
}

@media (max-width: 767px) and (prefers-reduced-motion: reduce) {
  #acl-mobile-navigation-panel.acl-mobile-navigation-panel {
    transition: none !important;
  }
}
</style>
